import type { SessionResponse } from '../ProfileScreen';
import { NotificationBell } from '../social/NotificationBell';
import { UserAvatar } from '../social/UserAvatar';
import {
  UserContextAvatarAction,
  useUserContextData,
} from '../userContext/UserContext';
import { HopperLogo } from './HopperLogo';
import { Icon } from './Icons';

export type ConsumerNavSection = 'feed' | 'map' | 'offers' | 'profile';

type NavItem = {
  label: string;
  icon: 'friends' | 'map' | 'offer' | 'profile';
  section: ConsumerNavSection;
};

export const CONSUMER_NAV_ITEMS: readonly NavItem[] = [
  { label: 'Пульс города', icon: 'friends', section: 'feed' },
  { label: 'Карта', icon: 'map', section: 'map' },
  { label: 'Предложения', icon: 'offer', section: 'offers' },
  { label: 'Профиль', icon: 'profile', section: 'profile' },
];

export const CONSUMER_PATH_BY_SECTION: Record<ConsumerNavSection, string> = {
  feed: '/pulse',
  map: '/',
  offers: '/offers',
  profile: '/profile',
};

export function DesktopHeader({
  cityName,
  activeSection,
  session,
  onNavigate,
  onNotifications,
  onCityClick,
  consistentAvatar = false,
}: {
  cityName: string;
  activeSection: ConsumerNavSection | null;
  session: SessionResponse | null;
  consistentAvatar?: boolean;
  onNavigate: (section: ConsumerNavSection) => void;
  onNotifications: () => void;
  onCityClick: () => void;
}) {
  const displayName = session?.authenticated ? session.user?.display_name?.trim() ?? '' : '';
  const initial = displayName ? displayName.slice(0, 1).toUpperCase() : null;

  return (
    <header className="desktop-header">
      <div className="desktop-header__brand-group">
        <a className="hopper-logo" href="/" aria-label="Хоппер — на главную">
          <HopperLogo variant="full" size={32} />
        </a>
        <button className="city-selector" type="button" onClick={onCityClick}>
          <Icon name="location" />
          <span>{cityName}</span>
          <Icon name="chevron-down" />
        </button>
      </div>

      <label className="global-search">
        <Icon name="search" />
        <input aria-label="Поиск заведений" placeholder="Поиск заведений" type="search" />
      </label>

      <nav className="desktop-nav" aria-label="Основная навигация">
        {CONSUMER_NAV_ITEMS.map((item) => (
          <button
            className={item.section === activeSection ? 'desktop-nav__item is-active' : 'desktop-nav__item'}
            data-shell-section={item.section}
            key={item.label}
            onClick={() => onNavigate(item.section)}
            type="button"
            aria-current={item.section === activeSection ? 'page' : undefined}
          >
            <Icon name={item.icon} />
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      <NotificationBell
        authenticated={session?.authenticated === true}
        className="desktop-notification-bell"
        onOpen={onNotifications}
      />

      {consistentAvatar ? (
        <UserAvatar
          displayName={displayName}
          avatarUrl={session?.authenticated ? session.user?.avatar_url : null}
          size={36}
          shape="rounded-square"
          ariaLabel={displayName ? `Профиль: ${displayName}` : 'Профиль пользователя'}
          onClick={() => onNavigate('profile')}
        />
      ) : (
        <button
          className="user-avatar"
          type="button"
          aria-label={displayName ? `Профиль: ${displayName}` : 'Профиль пользователя'}
          onClick={() => onNavigate('profile')}
        >
          {initial ?? <Icon name="profile" />}
        </button>
      )}
    </header>
  );
}

export function MobileHeader({
  cityName,
  userContext,
  onNotifications,
  onOpenUserContext,
  onCityClick,
  consistentAvatar = false,
}: {
  cityName: string;
  userContext: ReturnType<typeof useUserContextData>;
  onNotifications: () => void;
  onOpenUserContext: () => void;
  onCityClick: () => void;
  consistentAvatar?: boolean;
}) {
  return (
    <header className="mobile-header">
      <div className="mobile-header__leading">
        <a className="mobile-header__brand" href="/" aria-label="Хоппер — на карту">
          <HopperLogo variant="full" size={26} />
        </a>
        <button className="mobile-city" type="button" aria-label={`Выбрать город: ${cityName}`} onClick={onCityClick}>
          <Icon name="location" />
          <span>{cityName}</span>
          <Icon name="chevron-down" />
        </button>
      </div>
      <div className="mobile-header__actions">
        <NotificationBell authenticated={userContext.authenticated} onOpen={onNotifications} />
        <UserContextAvatarAction data={userContext} onOpen={onOpenUserContext} consistentAvatar={consistentAvatar} />
      </div>
    </header>
  );
}

export function MobileBottomNav({
  activeSection,
  onNavigate,
  onScan,
}: {
  activeSection: ConsumerNavSection | null;
  onNavigate: (section: ConsumerNavSection) => void;
  onScan: () => void;
}) {
  return (
    <nav className="mobile-bottom-nav" aria-label="Основная навигация">
      {CONSUMER_NAV_ITEMS.map((item, index) => (
        <button
          className={item.section === activeSection ? 'mobile-nav-item is-active' : 'mobile-nav-item'}
          data-shell-section={item.section}
          key={item.label}
          onClick={() => onNavigate(item.section)}
          style={index === 2 ? { gridColumn: 4 } : undefined}
          type="button"
          aria-current={item.section === activeSection ? 'page' : undefined}
        >
          <Icon name={item.icon} />
          <span>{item.label}</span>
          {index === 1 && <span className="mobile-nav-spacer" aria-hidden="true" />}
        </button>
      ))}
      <button className="scan-fab" type="button" aria-label="Сканировать чек" onClick={onScan}>
        <Icon name="scan" />
        <span>Скан</span>
      </button>
    </nav>
  );
}
